import React, { useMemo, useCallback } from 'react';
import { Grid, Tooltip } from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { genAttributes } from '@onward-delivery/core';
import { OnwardCard } from './Card';
import { toNational } from '@/utilities/formatPhoneNumber';
import deliveryStrings from '@/constants/deliveryStrings';
import { CardTitle, CardItem } from './blocks';
import { css } from '@emotion/react';
import { delWindowToFormattedDateRange, delWindowToFormattedDateSingle } from '@/utilities/delWindowToFormattedDate';
import zipcode_to_timezone from 'zipcode-to-timezone';
import { formatInTimeZone } from 'date-fns-tz';
import { asDateInTZ } from '@/utilities/convertToISO';
import { Header2 } from './Crossdocking/blocks';
import { PrimaryButton } from '@/styles/blocks';
import { useMutation } from '@apollo/client';
import { UPDATE_ORDER_BY_ID } from '@/graphql/mutations/orders';
import { captureException } from '@sentry/react';

export default function DropOffCard({ order, orderNotes }) {
    const { full_address, address, unit, zip, location, location_info, location_type } = genAttributes(order);
    const [updateOrder, { loading: savingDelayByConsignee }] = useMutation(UPDATE_ORDER_BY_ID, {
        onError: (err) => {
            console.error(err);
            captureException(err);
        },
    });

    const timeframe = useMemo(() => {
        if (!order) return '-';
        return order.del_window_start
            ? delWindowToFormattedDateRange(order.del_window_start, order.del_window_end, order[zip])
            : '-';
    }, [order, zip]);

    const formatTimestamp = useCallback(
        (datestr) => {
            if (!datestr || !order || !zip) return 'N/A';
            const tz = zipcode_to_timezone.lookup(order[zip]) || Intl.DateTimeFormat().resolvedOptions().timeZone;
            return formatInTimeZone(asDateInTZ(datestr, tz), tz, 'EEE, MMM d, yyyy');
        },
        [order, zip]
    );

    const isDelayedByConsignee = !!order?.delayed_by_consignee_ts;
    const delayedByConsigneeTooltip =
        'Clicking this button will indicate that this order has been delayed due to end customer actions (hold requests, scheduling issues).';

    return (
        <OnwardCard
            css={css`
                height: 100%;
            `}
            contentProps={{ style: { height: '100%' } }}
        >
            <Grid container style={{ height: '100%', flexWrap: 'nowrap' }} direction="column">
                <Header2
                    css={css`
                        margin-bottom: 24px;
                    `}
                >
                    {order.order_type === 'return' ? 'Return Pickup' : 'Delivery'}
                </Header2>
                <CardItem className="my-1">
                    {order.dropoff_name}
                    {['complete', 'routedComplete'].includes(order.order_status) && (
                        <CheckCircleIcon style={{ color: '#59B863' }} className="float-right" />
                    )}
                </CardItem>
                <CardItem className="my-1">{toNational(order.dropoff_phone)}</CardItem>
                <CardItem className="my-1">{order.dropoff_email || 'Email Not Provided'}</CardItem>
                <CardItem className="my-1">
                    {order[full_address] || `${order[address]}${order[unit] ? `, Unit ${order[unit]}` : ''}`}
                </CardItem>
                <CardItem className="my-1">
                    {order[location]}
                    {order[location_info] ? ', ' + order[location_info] : ''}
                    {order[location_type] ? ', ' + deliveryStrings.dropOffLocationType[order[location_type]] : ''}
                </CardItem>
                <CardItem className="my-1">
                    Scheduled Date:{' '}
                    {(order.order_type === 'return' ? order.pickup_date_formatted : order.delivery_date_formatted) ||
                        'TBD'}
                </CardItem>
                <CardItem className="my-1">Scheduled Timeframe: {timeframe}</CardItem>
                <CardItem className="my-1">
                    Arrival Time:{' '}
                    {order?.ltl_dropoff_arrival
                        ? formatTimestamp(order?.ltl_dropoff_arrival) +
                          ' ' +
                          delWindowToFormattedDateSingle(order?.ltl_dropoff_arrival, order[zip])
                        : 'N/A'}
                </CardItem>
                <CardItem className="my-1">
                    Completion Time:{' '}
                    {order?.completion_time
                        ? formatTimestamp(order?.completion_time) +
                          ' ' +
                          delWindowToFormattedDateSingle(order?.completion_time, order[zip])
                        : 'N/A'}
                </CardItem>
                <CardItem className="mt-1">
                    Comments:{' '}
                    {!!orderNotes?.delivery?.length ? orderNotes?.delivery.map((n) => n.note).join(' / ') : 'None'}
                </CardItem>
                {order?.special_instructions && (
                    <>
                        <CardItem className="mt-1">Special Instructions:</CardItem>
                        <CardItem
                            className="mt-1"
                            css={css`
                                margin-left: 1rem;
                            `}
                        >
                            {order?.special_instructions}
                        </CardItem>
                    </>
                )}
                <Grid
                    container
                    alignItems="center"
                    css={css`
                        margin-top: auto;
                        padding-top: 16px;
                        padding-bottom: 16px;
                    `}
                >
                    <Grid item>
                        <PrimaryButton
                            red
                            loading={savingDelayByConsignee}
                            disabled={isDelayedByConsignee}
                            onClick={() => {
                                if (isDelayedByConsignee) return;
                                updateOrder({
                                    variables: {
                                        order_id: order.order_id,
                                        update: {
                                            delayed_by_consignee_ts: new Date().toISOString(),
                                        },
                                    },
                                });
                            }}
                        >
                            {isDelayedByConsignee ? 'Marked as Delayed by Consignee' : 'Delayed by Consignee'}
                        </PrimaryButton>
                    </Grid>
                    <Grid item>
                        <Tooltip title={delayedByConsigneeTooltip}>
                            <InfoOutlinedIcon
                                css={css`
                                    margin-left: 8px;
                                    color: rgba(0, 0, 0, 0.54);
                                `}
                            />
                        </Tooltip>
                    </Grid>
                </Grid>
            </Grid>
        </OnwardCard>
    );
}
